import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Globe, X } from 'lucide-react';
import { useLocale } from '../i18n/LocaleContext';
import { browserLocale, storedLocale } from '../i18n/locales';
import { alternatesFor } from '../i18n/routes';
import { loadLocale, makeT } from '../i18n/translate';

/**
 * On an English page that exists in the visitor's browser language, offer it, in
 * that language. Offered, not redirected: a search engine reading the English page
 * must get the English page. Someone who picked a language (or closed this) is not
 * asked again.
 */
export default function LanguageSuggestion() {
  const { locale, setPreferred } = useLocale();
  const { pathname } = useLocation();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    // After mount only: the prerendered page must not change under hydration
    const wanted = browserLocale();
    const alternates = alternatesFor(pathname);
    const chosen = storedLocale();
    if (!(locale === 'en' && wanted && wanted !== 'en' && alternates?.paths[wanted] && (!chosen || chosen === wanted))) {
      setTarget(null);
      return undefined;
    }
    // Offered in its own language, so that language is fetched first (and is then
    // already here if they take the offer)
    let current = true;
    loadLocale(wanted)
      .then(() => { if (current) setTarget({ locale: wanted, path: alternates.paths[wanted] }); })
      .catch(() => {});
    return () => { current = false; };
  }, [locale, pathname]);

  if (!target) return null;
  const t = makeT(target.locale);
  return (
    <div lang={target.locale} className="relative z-40 border-b border-brand/20 bg-indigo-500/10 text-sm">
      <div className="container mx-auto flex items-center justify-center gap-3 px-10 py-2 text-center">
        <Globe className="hidden sm:block h-4 w-4 text-brand" aria-hidden="true" />
        <span className="text-foreground">{t('suggest.text')}</span>
        <Link to={target.path} onClick={() => setPreferred(target.locale)} className="font-semibold text-brand hover:underline">
          {t('suggest.link')}
        </Link>
        <button
          type="button"
          onClick={() => { setPreferred('en'); setTarget(null); }}
          aria-label={t('suggest.close')}
          className="absolute end-3 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
